import { z } from 'zod';

const color = z.string().max(40);
const style = {
  color: color.default('#FFFFFF'),
  background: color.default('#0F172A'),
  fontFamily: z.string().max(60).default('Inter'),
};

export const widgetConfigSchemas = {
  clock: z.object({
    ...style,
    style: z.enum(['digital', 'analog']).default('digital'),
    showSeconds: z.boolean().default(false),
    showDate: z.boolean().default(true),
    timezone: z.string().max(60).optional(),
  }),
  date: z.object({ ...style, format: z.enum(['long', 'short', 'weekday']).default('long') }),
  weather: z.object({
    ...style,
    background: color.default('#1E3A8A'),
    lat: z.number().min(-90).max(90).optional(),
    lng: z.number().min(-180).max(180).optional(),
    locationName: z.string().max(80).optional(),
    units: z.enum(['c', 'f']).default('c'),
    days: z.number().int().min(0).max(5).default(3),
  }),
  rss: z.object({
    ...style,
    url: z.string().url().max(2000),
    title: z.string().max(80).optional(),
    maxItems: z.number().int().min(1).max(30).default(8),
    mode: z.enum(['list', 'ticker', 'single']).default('list'),
    showDescription: z.boolean().default(false),
    secondsPerItem: z.number().min(3).max(120).default(10),
    refreshMinutes: z.number().int().min(5).max(1440).default(15),
  }),
  ticker: z.object({
    ...style,
    text: z.string().min(1).max(2000),
    speed: z.number().min(10).max(800).default(120),
    fontSize: z.number().min(10).max(300).default(48),
    separator: z.string().max(10).default('  •  '),
  }),
  countdown: z.object({
    ...style,
    target: z.string().min(10).max(40),
    title: z.string().max(120).default(''),
    doneText: z.string().max(120).default(''),
    showSeconds: z.boolean().default(true),
  }),
  qr: z.object({
    value: z.string().min(1).max(2000),
    label: z.string().max(120).optional(),
    color: color.default('#0F172A'),
    background: color.default('#FFFFFF'),
  }),
  webpage: z.object({
    url: z.string().url().max(2000),
    refreshSec: z.number().int().min(0).max(86400).default(0),
    zoom: z.number().min(0.25).max(3).default(1),
  }),
  youtube: z.object({
    url: z.string().max(2000),
    mute: z.boolean().default(true),
    loop: z.boolean().default(true),
    captions: z.boolean().default(false),
  }),
  vimeo: z.object({
    url: z.string().max(2000),
    mute: z.boolean().default(true),
    loop: z.boolean().default(true),
  }),
  'google-slides': z.object({
    url: z.string().url().max(2000),
    delayMs: z.number().int().min(2000).max(600000).default(10000),
  }),
  'sheet-table': z.object({
    ...style,
    color: color.default('#0F172A'),
    background: color.default('#FFFFFF'),
    url: z.string().url().max(2000),
    title: z.string().max(80).optional(),
    headerRow: z.boolean().default(true),
    maxRows: z.number().int().min(1).max(100).default(15),
    refreshMinutes: z.number().int().min(1).max(1440).default(10),
    headerBackground: color.default('#4F46E5'),
    headerColor: color.default('#FFFFFF'),
  }),
  'social-wall': z.object({
    url: z.string().url().max(2000),
    refreshSec: z.number().int().min(0).max(86400).default(600),
  }),
  queue: z.object({
    ...style,
    background: color.default('#4F46E5'),
    title: z.string().max(80).default('Nu aan de beurt'),
    prefix: z.string().max(5).default(''),
    current: z.number().int().min(0).max(99999).default(1),
    counter: z.string().max(40).optional(),
  }),
  menu: z.object({
    ...style,
    color: color.default('#1F2937'),
    background: color.default('#FFFBEB'),
    accent: color.default('#B45309'),
    title: z.string().max(80).default('Menu'),
    currency: z.string().max(5).default('€'),
    columns: z.number().int().min(1).max(4).default(2),
    sections: z
      .array(
        z.object({
          name: z.string().max(80),
          items: z
            .array(
              z.object({
                name: z.string().max(120),
                description: z.string().max(240).optional(),
                price: z.string().max(20),
              }),
            )
            .max(40),
        }),
      )
      .max(12)
      .default([]),
  }),
  holidays: z.object({
    ...style,
    country: z.string().length(2).default('NL'),
    title: z.string().max(80).default('Binnenkort'),
    daysAhead: z.number().int().min(1).max(365).default(30),
    birthdays: z
      .array(z.object({ name: z.string().max(80), date: z.string().regex(/^\d{2}-\d{2}$/) }))
      .max(500)
      .default([]),
    showHolidays: z.boolean().default(true),
  }),
  'dashboard-url': z.object({
    url: z.string().url().max(2000),
    refreshSec: z.number().int().min(0).max(86400).default(300),
    zoom: z.number().min(0.25).max(3).default(1),
  }),
  html: z.object({
    html: z.string().max(100_000),
    css: z.string().max(50_000).default(''),
    allowScripts: z.boolean().default(false),
  }),
} as const;

export type WidgetKind = keyof typeof widgetConfigSchemas;
export type WidgetConfig<K extends WidgetKind> = z.infer<(typeof widgetConfigSchemas)[K]>;

export function parseWidgetConfig<K extends WidgetKind>(type: K, config: unknown): WidgetConfig<K> {
  return widgetConfigSchemas[type].parse(config ?? {}) as WidgetConfig<K>;
}

export function safeWidgetConfig<K extends WidgetKind>(
  type: K,
  config: unknown,
): WidgetConfig<K> | null {
  const r = widgetConfigSchemas[type].safeParse(config ?? {});
  return r.success ? (r.data as WidgetConfig<K>) : null;
}

/** Dutch public holidays (fixed + Easter-based) for a given year. */
export function dutchHolidays(year: number): Array<{ date: string; nl: string; en: string }> {
  const easter = easterSunday(year);
  const add = (d: Date, days: number) =>
    new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + days));
  const fmt = (d: Date) => d.toISOString().slice(0, 10);
  // King's Day moves to the 26th when the 27th is a Sunday.
  const kingsDay = new Date(Date.UTC(year, 3, 27));
  if (kingsDay.getUTCDay() === 0) kingsDay.setUTCDate(26);
  const list = [
    { date: `${year}-01-01`, nl: 'Nieuwjaarsdag', en: "New Year's Day" },
    { date: fmt(add(easter, -2)), nl: 'Goede Vrijdag', en: 'Good Friday' },
    { date: fmt(easter), nl: 'Eerste paasdag', en: 'Easter Sunday' },
    { date: fmt(add(easter, 1)), nl: 'Tweede paasdag', en: 'Easter Monday' },
    { date: fmt(kingsDay), nl: 'Koningsdag', en: "King's Day" },
    { date: `${year}-05-05`, nl: 'Bevrijdingsdag', en: 'Liberation Day' },
    { date: fmt(add(easter, 39)), nl: 'Hemelvaartsdag', en: 'Ascension Day' },
    { date: fmt(add(easter, 49)), nl: 'Eerste pinksterdag', en: 'Whit Sunday' },
    { date: fmt(add(easter, 50)), nl: 'Tweede pinksterdag', en: 'Whit Monday' },
    { date: `${year}-12-25`, nl: 'Eerste kerstdag', en: 'Christmas Day' },
    { date: `${year}-12-26`, nl: 'Tweede kerstdag', en: 'Boxing Day' },
  ];
  return list.sort((a, b) => a.date.localeCompare(b.date));
}

/** Anonymous Gregorian algorithm. */
export function easterSunday(year: number): Date {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(Date.UTC(year, month - 1, day));
}

/** Extracts a YouTube video id from common URL shapes or returns the input if it looks like an id. */
export function youtubeId(input: string): string | null {
  const m =
    /(?:youtu\.be\/|youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/))([A-Za-z0-9_-]{11})/.exec(
      input,
    );
  if (m) return m[1]!;
  return /^[A-Za-z0-9_-]{11}$/.test(input) ? input : null;
}

export function vimeoId(input: string): string | null {
  const m = /vimeo\.com\/(?:video\/)?(\d{6,12})/.exec(input);
  if (m) return m[1]!;
  return /^\d{6,12}$/.test(input) ? input : null;
}

/** Parses simple CSV (RFC 4180 quotes) into rows. */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let quoted = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i]!;
    if (quoted) {
      if (ch === '"' && text[i + 1] === '"') {
        field += '"';
        i++;
      } else if (ch === '"') quoted = false;
      else field += ch;
    } else if (ch === '"') quoted = true;
    else if (ch === ',' || ch === ';') {
      row.push(field);
      field = '';
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && text[i + 1] === '\n') i++;
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
    } else field += ch;
  }
  if (field.length || row.length) {
    row.push(field);
    rows.push(row);
  }
  return rows.filter((r) => r.some((c) => c.trim().length));
}
